import { useTranslation } from '@exam-room/translations';
import { useRef, type KeyboardEvent } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';

import { Alert, Button, Form, LoadingSpinner } from '../../../../components';

import { type LoginFormInput } from './schema';

interface LoginFormProps {
  onSubmit: SubmitHandler<LoginFormInput>;
  isLoading: boolean;
}

function LoginForm({ onSubmit, isLoading }: LoginFormProps) {
  const examCenterCodeRef = useRef<HTMLInputElement>(null);
  const examYearMonthRef = useRef<HTMLInputElement>(null);
  const examDateRef = useRef<HTMLInputElement>(null);
  const roundNumberRef = useRef<HTMLInputElement>(null);

  const handleKeyUp = (
    e: KeyboardEvent<HTMLInputElement>,
    maxLength: number,
    nextRef?: React.RefObject<HTMLInputElement>,
  ) => {
    const target = e.target as HTMLInputElement;
    if (target.value.length >= maxLength && nextRef?.current) {
      nextRef.current.focus();
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInput>({
    defaultValues: {
      idCardNumber: '',
      examCenterCode: '',
      examYearMonth: '',
      examDate: '',
      roundNumber: '',
      password: '',
    },
  });
  const { t } = useTranslation();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='grid gap-6'>
      <Form.Input
        required
        rules={{ required: t('login.idNumber.required') }}
        control={control}
        id='idCardNumber'
        name='idCardNumber'
        label={t('login.idNumber.label')}
        placeholder={t('login.idNumber.placeholder')}
        autoComplete='off'
        type='text'
      />
      <div className='flex w-[420px] flex-col'>
        <div className='flex w-[420px] items-end gap-2'>
          <Form.Input
            className='w-20'
            required
            control={control}
            id='examCenterCode'
            name='examCenterCode'
            label={t('login.examId.label')}
            type='text'
            maxLength={3}
            placeholder='000'
            ref={examCenterCodeRef}
            autoComplete='off'
            onKeyUp={(e) => handleKeyUp(e, 3, examYearMonthRef)}
          />
          <h5 className='h-10 self-end'>-</h5>
          <Form.Input
            required
            control={control}
            id='examYearMonth'
            name='examYearMonth'
            type='text'
            maxLength={6}
            placeholder='000000'
            ref={examYearMonthRef}
            autoComplete='off'
            onKeyUp={(e) => handleKeyUp(e, 6, examDateRef)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && (e.target as HTMLInputElement).value.length === 0) {
                examCenterCodeRef.current?.focus();
              }
            }}
          />
          <h5 className='h-10 self-end'>-</h5>
          <Form.Input
            className='w-[56px]'
            required
            control={control}
            id='examDate'
            name='examDate'
            type='text'
            maxLength={2}
            placeholder='00'
            ref={examDateRef}
            autoComplete='off'
            onKeyUp={(e) => handleKeyUp(e, 2, roundNumberRef)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && (e.target as HTMLInputElement).value.length === 0) {
                examYearMonthRef.current?.focus();
              }
            }}
          />
          <h5 className='h-10 self-end'>-</h5>
          <Form.Input
            className='w-20'
            required
            control={control}
            id='roundNumber'
            name='roundNumber'
            type='text'
            maxLength={3}
            placeholder='000'
            ref={roundNumberRef}
            autoComplete='off'
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && (e.target as HTMLInputElement).value.length === 0) {
                examDateRef.current?.focus();
              }
            }}
          />
        </div>
        {errors.examCenterCode || errors.examYearMonth || errors.examDate || errors.roundNumber ? (
          <p className='text-danger mt-2 text-start text-sm'>{t('login.examId.required')}</p>
        ) : null}
      </div>
      <Form.Input
        required
        rules={{ required: t('login.password.required') }}
        control={control}
        id='password'
        name='password'
        label={t('login.password.label')}
        type='password'
        autoComplete='off'
        placeholder={t('login.password.placeholder')}
      />
      <Alert variant='warning' message={t('login.alert.examDetails')} />
      <Button type='submit' className='mx-auto mt-2 w-full' disabled={isLoading}>
        {isLoading ? <LoadingSpinner className='h-7 w-7' /> : t('login.submit')}
      </Button>
    </form>
  );
}

export default LoginForm;
